import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import {
  Z_HEAD,
  Z_STICKY,
  GH,
  RH,
  thCoreSC,
  tdBaseSC,
  CARD_STYLE_SC,
  CARD_HEAD_STYLE_SC,
  CARD_TITLE_STYLE_SC,
  CARD_META_STYLE_SC,
  SC_ACCENT,
  CT_OP_ENTRIES,
  ctApplyNumFilter,
  fpBase,
  fpLbl,
  fpTxtI,
  fpNumI,
  fpOpBt,
  fpClr,
  opDropS,
  opItm,
  ctFBtnSt,
  FilterIcon,
  ColumnPicker,
} from "./yoyTableShared";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";

import {
  EVEN_BG,
  ODD_BG,
} from "../../reportTableConfig";

/* ── header / cell styles (no fixed column widths) ── */
const BOLD_BG = "#F0F4F8";

const thEl = {
  ...thCoreSC(0, GH, Z_HEAD + 2, "0 14px", 1),
  position: "sticky",
  left: 0,
  zIndex: Z_HEAD + 2,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "left",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  maxWidth: 280,
};
const thItd = {
  ...thCoreSC(0, GH, Z_HEAD, "0 10px", 1),
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "right",
  whiteSpace: "nowrap",
};
const thNum = {
  ...thCoreSC(0, GH, Z_HEAD, "0 10px", 1),
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "right",
  whiteSpace: "nowrap",
};
const thTot = {
  ...thNum,
  color: "#F59E0B",
};

/* ── data cell styles ── */
const cell = (bg, align, fw, extra) => ({
  ...tdBaseSC(bg, align, fw),
  color: fw >= 600 ? "#111827" : "#334155",
  ...extra,
});
const tdEl = (bg, fw) => ({
  ...cell(bg, "left", fw, {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    fontVariantNumeric: "normal",
    maxWidth: 280,
  }),
  position: "sticky",
  left: 0,
  zIndex: Z_STICKY,
  padding: "7px 14px",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
});
const tdItd = (bg, fw) => cell(bg, "right", fw, { whiteSpace: "nowrap" });
const tdVal = (bg, fw) => cell(bg, "right", fw, { whiteSpace: "nowrap" });
const tdTot = (fw) =>
  cell(
    fw >= 600 ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.03)",
    "right",
    fw >= 600 ? 700 : 600,
    { color: "#F59E0B", whiteSpace: "nowrap" },
  );

/* ── formatting ── */
const fmtPc = (v) => (v == null ? "" : `${Number(v).toFixed(1)}%`);
const fmtPlain = (v, dec = 1) =>
  v == null
    ? ""
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      });
const fmtMoney = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
const fmtAuto = (v, row) => {
  if (v == null) return "";
  if (row.isPct || row.lineItemKey === "grossMarginPct") return fmtPc(v);
  if (row.isCurrency) return fmtMoney(v);
  if (row.dp != null) return fmtPlain(v, row.dp);
  return fmtPlain(v, 1);
};
const fmtItdVal = (v, row) => fmtAuto(v, row);

export default function FullPlanTab({ FP_YEARS, FP_ROWS }) {
  const YEAR_COLS = useMemo(
    () => FP_YEARS.map((y, i) => ({ key: `y${i}`, label: String(y), idx: i })),
    [FP_YEARS],
  );

  const [visibleYears, setVisibleYears] = useState(YEAR_COLS.map((c) => c.key));
  useEffect(() => {
    setVisibleYears(YEAR_COLS.map((c) => c.key));
  }, [YEAR_COLS]);
  const activeYearCols = YEAR_COLS.filter((c) => visibleYears.includes(c.key));
  const toggleYear = (key) =>
    setVisibleYears((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((k) => k !== key)
          : prev
        : [...prev, key].sort(
            (a, b) =>
              YEAR_COLS.findIndex((c) => c.key === a) -
              YEAR_COLS.findIndex((c) => c.key === b),
          ),
    );

  const [sortKeys, setSortKeys] = useState([]);
  const [labelFilter, setLabelFilter] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  /* Total re-derived from the currently visible year columns only — hiding a
     year should shrink the total, not silently keep summing it in. Gross
     Margin % and FTE are ratios re-derived server-side from components this
     table doesn't have (revenue/profit, hours/working-days), not a sum of
     yearly values, so re-deriving them from a partial year subset here would
     produce a wrong number rather than a merely different one — keep the
     server total for those two rows. */
  const visibleTotal = useCallback(
    (row) => {
      if (row.isPct || row.lineItemKey === "fte") return row.total;
      return activeYearCols.reduce(
        (sum, c) => sum + (Number(row.ys[c.idx]) || 0),
        0,
      );
    },
    [activeYearCols],
  );

  const getSortValue = useCallback((row, colKey) => {
    if (colKey === "label") return row.label;
    if (colKey === "itd") return row.itd;
    if (colKey === "total") return visibleTotal(row);
    if (colKey.startsWith("y")) {
      const idx = Number(colKey.slice(1));
      return row.ys[idx];
    }
    return null;
  }, [visibleTotal]);

  const getSortColumnLabel = useCallback(
    (colKey) => {
      if (colKey === "label") return "Element";
      if (colKey === "itd") return "ITD";
      if (colKey === "total") return "Total";
      const col = YEAR_COLS.find((c) => c.key === colKey);
      return col ? col.label : colKey;
    },
    [YEAR_COLS],
  );

  const hasFilterValue = (key) => {
    if (key === "label") return !!labelFilter;
    return !!colFilters[key]?.value;
  };

  const toggleFilter = (key, btnEl) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    setFilterPos(calcFilterPos(btnEl, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const renderFilterContent = (key) => {
    if (key === "label") {
      return (
        <TextFilterContent
          label="Element"
          value={labelFilter}
          onChange={setLabelFilter}
          onClear={() => setLabelFilter("")}
        />
      );
    }
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getSortColumnLabel(key)}
        value={f.value}
        op={f.op}
        onValueChange={(v) => setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))}
        onClear={() => setColFilters((p) => { const n = { ...p }; delete n[key]; return n; })}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  const filteredRows = useMemo(() => {
    const q = labelFilter.trim().toLowerCase();
    return FP_ROWS.filter((row) => {
      if (q && !String(row.label ?? "").toLowerCase().includes(q)) return false;
      for (const [key, f] of Object.entries(colFilters)) {
        if (!f?.value) continue;
        let v = null;
        if (key === "total") v = visibleTotal(row);
        else if (key === "itd") v = row.itd;
        else if (key.startsWith("y")) v = row.ys[Number(key.slice(1))];
        if (!ctApplyNumFilter(v, f)) return false;
      }
      return true;
    });
  }, [FP_ROWS, labelFilter, colFilters, visibleTotal]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const yearRangeLabel =
    FP_YEARS.length > 0
      ? `FY${FP_YEARS[0]} to FY${FP_YEARS[FP_YEARS.length - 1]}`
      : "";

  const tableStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "auto",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 12,
    width: "max-content",
    minWidth: "100%",
  };

  const renderHeaderCell = (key, label, style, align) => {
    const active = openFilterKey === key || hasFilterValue(key);
    const filterable =
      key === "label" || key === "itd" || key === "total" || key.startsWith("y");
    return (
      <th
        key={key}
        style={{ ...style, cursor: "pointer" }}
        onClick={onThClick(key)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: align === "right" ? "flex-end" : "space-between",
            gap: 4,
            minWidth: 0,
          }}
        >
          <WaterfallThLabel
            label={label}
            colKey={key}
            sortKeys={sortKeys}
            getLabel={getSortColumnLabel}
            align={align}
          />
          {filterable && (
            <button
              ref={(el) => {
                filterBtnRefs.current[key] = el;
              }}
              data-filter-btn
              style={ctFBtnSt(active)}
              onClick={(e) => {
                e.stopPropagation();
                toggleFilter(key, e.currentTarget);
              }}
            >
              <FilterIcon />
            </button>
          )}
        </div>
      </th>
    );
  };

  return (
    <div style={CARD_STYLE_SC}>
      <div style={CARD_HEAD_STYLE_SC}>
        <div>
          <h2 style={CARD_TITLE_STYLE_SC}>
            Full Plan over Year{yearRangeLabel ? ` — ${yearRangeLabel}` : ""}
          </h2>
          {sortKeys.length > 0 && (
            <div style={CARD_META_STYLE_SC}>
              <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                sorted by{" "}
                {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
              </span>
            </div>
          )}
        </div>
        <ColumnPicker
          title="Visible Years"
          items={YEAR_COLS}
          visible={visibleYears}
          onToggle={toggleYear}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ height: GH }}>
              {renderHeaderCell("label", "Element", thEl, "left")}
              {renderHeaderCell("itd", "ITD", thItd, "right")}
              {activeYearCols.map((c) =>
                renderHeaderCell(c.key, c.label, thNum, "right"),
              )}
              {renderHeaderCell("total", "Total", thTot, "right")}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, ri) => {
              const bg = row.bold ? BOLD_BG : ri % 2 === 0 ? EVEN_BG : ODD_BG;
              const fw = row.bold ? 700 : 400;

              if (row.single !== false) {
                return (
                  <tr key={row.label + ri} style={{ minHeight: RH }}>
                    <td style={tdEl(bg, fw)}>{row.label}</td>
                    <td style={tdItd(bg, fw)}>{fmtItdVal(row.itd, row)}</td>
                    {activeYearCols.map((c) => (
                      <td key={c.key} style={tdVal(bg, fw)}>
                        {fmtAuto(row.ys[c.idx], row)}
                      </td>
                    ))}
                    <td style={tdTot(fw)}>{fmtAuto(visibleTotal(row), row)}</td>
                  </tr>
                );
              }

              return (
                <React.Fragment key={row.label + ri}>
                  <tr style={{ minHeight: RH }}>
                    <td style={tdEl(bg, fw)}>{row.label}</td>
                    <td style={tdItd(bg, fw)}>{fmtItdVal(row.itd, row)}</td>
                    {activeYearCols.map((c) => (
                      <td key={c.key} style={tdVal(bg, 400)} />
                    ))}
                    <td style={tdTot(fw)} />
                  </tr>
                  <tr style={{ minHeight: RH }}>
                    <td style={tdEl(bg, 400)} />
                    <td style={{ ...tdItd(bg, 400), background: bg }} />
                    {activeYearCols.map((c) => (
                      <td key={c.key} style={tdVal(bg, 400)}>
                        {fmtAuto(row.ys[c.idx], row)}
                      </td>
                    ))}
                    <td style={tdTot(400)}>{fmtAuto(visibleTotal(row), row)}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Filter panel portal */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* Operator dropdown portal */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || { op: "gt" }).op}
            onSelect={(op) => {
              setColFilters((p) => ({
                ...p,
                [openOpKey]: { ...(p[openOpKey] || { value: "" }), op },
              }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
